import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Icon from '../components/ui/Icon'
import AuthGuard from '../components/AuthGuard'
import ProfileCompletionGuard from '../components/ProfileCompletionGuard'
import apiService from '../services/api'

const Communities = () => {
  return (
    <AuthGuard 
      requireAuth={true}
      title="কমিউনিটি ম্যানেজমেন্ট"
      description="আপনার কমিউনিটি তৈরি এবং পরিচালনা করুন।"
    >
      {({ user, logout, openAuth }) => <CommunitiesContent user={user} logout={logout} openAuth={openAuth} />}
    </AuthGuard>
  )
}

const CommunitiesContent = ({ user, logout, openAuth }) => {
  const navigate = useNavigate()
  const [communities, setCommunities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Create Community Modal State
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [communityName, setCommunityName] = useState('')
  const [communityDescription, setCommunityDescription] = useState('')
  const [isPublic, setIsPublic] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)

  // Add Member Modal State  
  const [showAddMemberModal, setShowAddMemberModal] = useState(false)
  const [selectedCommunityId, setSelectedCommunityId] = useState(null)
  const [memberPhoneNumber, setMemberPhoneNumber] = useState('')
  const [addMemberLoading, setAddMemberLoading] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const communitiesData = await apiService.getUserCommunities()
      setCommunities(communitiesData.communities || [])
    } catch (err) {
      console.error('Failed to load community data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateCommunity = async (e) => {
    e.preventDefault()
    if (!communityName.trim()) return

    try {
      setCreateLoading(true)
      const result = await apiService.createCommunity(
        communityName.trim(),
        communityDescription.trim(),
        isPublic
      )

      // Add the new community to the list
      setCommunities(prev => [...prev, result.community])
      setCommunityName('')
      setCommunityDescription('')
      setIsPublic(false)
      setShowCreateModal(false)
    } catch (err) {
      console.error('Failed to create community:', err)
      alert('Failed to create community: ' + err.message)
    } finally {
      setCreateLoading(false)
    }
  }

  const handleAddMember = async (e) => {
    e.preventDefault()
    if (!memberPhoneNumber.trim() || !selectedCommunityId) return

    try {
      setAddMemberLoading(true)
      const response = await apiService.addCommunityMember(selectedCommunityId, memberPhoneNumber.trim())

      setMemberPhoneNumber('')
      setShowAddMemberModal(false)
      alert(response.message || 'সদস্য সফলভাবে যোগ করা হয়েছে!')
      // Reload communities to update member count
      loadData()
    } catch (err) {
      console.error('Failed to add member:', err)
      alert('Failed to add member: ' + err.message)
    } finally {
      setAddMemberLoading(false)
    }
  }


  const openAddMemberModal = (communityId) => {
    setSelectedCommunityId(communityId)
    setMemberPhoneNumber('')
    setShowAddMemberModal(true)
  }

  const handleLeaveCommunity = async (communityId, communityName) => {
    if (!window.confirm(`আপনি কি নিশ্চিত যে আপনি "${communityName}" কমিউনিটি ছেড়ে যেতে চান?`)) {
      return
    }

    try {
      const response = await apiService.leaveCommunity(communityId)
      alert(response.message || 'কমিউনিটি সফলভাবে ছেড়ে দিয়েছেন')
      // Reload communities to update the list
      loadData()
    } catch (err) {
      console.error('Failed to leave community:', err)
      alert('Failed to leave community: ' + err.message)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading communities...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <strong>Error:</strong> {error}
            </div>
            <Button onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <ProfileCompletionGuard>
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">কমিউনিটি</h1>
            <p className="text-muted-foreground mb-4">আপনার নিজস্ব কমিউনিটি তৈরি করুন এবং পরিচিত মানুষদের সাথে জাগিয়ে দেওয়ার সেবা করুন।</p>
            
            {/* Instructions Card */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
              <div className="flex">
                <Icon name="Info" className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-800 mb-2">কমিউনিটি ফিচার কীভাবে কাজ করে?</h3>
                  <div className="text-blue-700 text-sm space-y-2">
                    <p>• <strong>কমিউনিটি তৈরি করুন:</strong> পরিবার, বন্ধু, বা অফিসের সহকর্মীদের নিয়ে গ্রুপ তৈরি করুন।</p>
                    <p>• <strong>সদস্য যোগ করুন:</strong> ফোন নাম্বার দিয়ে অন্যদের আমন্ত্রণ পাঠান। তারা স্বীকৃতি দিলে জয়েন হবেন।</p>
                    <p>• <strong>কমিউনিটি জাগান:</strong> 'কমিউনিটি জাগান' বিভাগে গিয়ে আপনার গ্রুপের সদস্যদেরকে প্রাধান্য দিয়ে জাগান।</p>
                    <p>• <strong>সদস্য দেখুন:</strong> প্রত্যেক কমিউনিটির সদস্যদের তালিকা ও কলের ইতিহাস দেখুন।</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Communities Content */}
          <div>
              {/* Action Buttons */}
              <div className="mb-6 flex flex-wrap gap-3">
                <Button onClick={() => setShowCreateModal(true)}>
                  <Icon name="Plus" className="w-4 h-4 mr-2" />
                  নতুন কমিউনিটি তৈরি করুন
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/communities/discover')}
                >
                  <Icon name="Search" className="w-4 h-4 mr-2" />
                  পাবলিক কমিউনিটি খুঁজুন
                </Button>
              </div>

              {/* Communities List */}
              {communities.length === 0 ? (
                <Card className="text-center py-12">
                  <Icon name="Users" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">কোন কমিউনিটি নেই</h3>
                  <p className="text-muted-foreground mb-4">
                    আপনার প্রথম কমিউনিটি তৈরি করুন এবং পরিবার ও বন্ধুদের যোগ করুন।
                  </p>
                  <Button onClick={() => setShowCreateModal(true)}>
                    কমিউনিটি তৈরি করুন
                  </Button>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {communities.map((community) => (
                    <Card key={community.id} className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold">{community.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {community.is_creator ? 'আপনার তৈরি' : `তৈরি করেছেন: ${community.creator_name}`}
                          </p>
                        </div>
                        <button
                          onClick={() => openAddMemberModal(community.id)}
                          className="text-primary hover:text-primary-hover flex items-center text-sm whitespace-nowrap"
                        >
                          <Icon name="Plus" className="w-4 h-4 mr-1 flex-shrink-0" />
                          <span className="hidden sm:inline">Add Member</span>
                          <span className="sm:hidden">যোগ করুন</span>
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Icon name="Users" className="w-4 h-4 mr-1" />
                          {community.member_count} সদস্য
                        </div>
                        <div className="text-sm text-muted-foreground">
                          যোগদান: {community.joined_date}
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t space-y-2">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/communities/${community.id}/members`)}
                            className="flex-1"
                          >
                            <Icon name="Users" className="w-4 h-4 mr-2" />
                            সদস্য
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => navigate('/community-wake-up')}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <Icon name="Phone" className="w-4 h-4 mr-2" />
                            জাগান
                          </Button>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const inviteLink = `${window.location.origin}/communities/join/${community.invite_code}`;
                            navigator.clipboard.writeText(inviteLink);
                            alert('আমন্ত্রণ লিঙ্ক কপি হয়েছে!');
                          }}
                          className="w-full"
                        >
                          <Icon name="Link" className="w-4 h-4 mr-2" />
                          আমন্ত্রণ লিঙ্ক কপি করুন
                        </Button>
                        {!community.is_creator && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleLeaveCommunity(community.id, community.name)}
                            className="w-full text-red-600 hover:text-red-700 hover:border-red-300"
                          >
                            <Icon name="LogOut" className="w-4 h-4 mr-2" />
                            কমিউনিটি ছেড়ে দিন
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

        </div>
      </div>

      {/* Create Community Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">নতুন কমিউনিটি তৈরি করুন</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateCommunity}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  কমিউনিটির নাম
                </label>
                <input
                  type="text"
                  value={communityName}
                  onChange={(e) => setCommunityName(e.target.value)}
                  placeholder="যেমন: পারিবারিক গ্রুপ, অফিস টিম"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  maxLength={100}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  বর্ণনা (ঐচ্ছিক)
                </label>
                <textarea
                  value={communityDescription}
                  onChange={(e) => setCommunityDescription(e.target.value)}
                  placeholder="কমিউনিটি সম্পর্কে সংক্ষিপ্ত বর্ণনা লিখুন..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={3}
                  maxLength={500}
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <span className="text-sm font-medium">
                    পাবলিক কমিউনিটি (মানুষ আপনার গ্রুপ দেখতে এবং যোগ দিতে পারবে)
                  </span>
                </label>
              </div>

              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                >
                  বাতিল
                </Button>
                <Button
                  type="submit"
                  disabled={createLoading || !communityName.trim()}
                  className="flex-1"
                >
                  {createLoading ? 'তৈরি হচ্ছে...' : 'তৈরি করুন'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">সদস্য যোগ করুন</h3>
              <button
                onClick={() => setShowAddMemberModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddMember}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  ফোন নাম্বার
                </label>
                <input
                  type="tel"
                  value={memberPhoneNumber}
                  onChange={(e) => setMemberPhoneNumber(e.target.value)}
                  placeholder="+8801812345678 or 01812345678"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  pattern="[+]?[0-9]{10,15}"
                  minLength={10}
                  maxLength={15}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  দেশীয় বা আন্তর্জাতিক নাম্বার (দেশের কোড সহ বা ছাড়া)
                </p>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddMemberModal(false)}
                  className="flex-1"
                >
                  বাতিল
                </Button>
                <Button
                  type="submit"
                  disabled={addMemberLoading || !memberPhoneNumber.trim()}
                  className="flex-1"
                >
                  {addMemberLoading ? 'পাঠানো হচ্ছে...' : 'রিকোয়েস্ট পাঠান'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
        <Footer />
      </div>
    </ProfileCompletionGuard>
  )
}

export default Communities